// webserver/src/components/GoogleHomeDevices.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AssistantQuery from './AssistantQuery';
import './GoogleHomeDevices.css';

const GoogleHomeDevices = ({ currentDevice, currentArea, assistantLoginTrigger = 0 }) => {
  console.log("[GoogleHomeDevices] Mounted. currentDevice prop:", currentDevice);
  console.log("[GoogleHomeDevices] currentArea prop:", currentArea);

  const [isConnected, setIsConnected] = useState(!!localStorage.getItem('googleConnected'));
  const [assistantConnected, setAssistantConnected] = useState(!!localStorage.getItem('assistantConnected'));
  const [homeNeedsReauth, setHomeNeedsReauth] = useState(false);
  const [assistantNeedsReauth, setAssistantNeedsReauth] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [globalResponse, setGlobalResponse] = useState('');

  // For TV controls: use currentDevice if available and type "TV"
  const [selectedTVDevice, setSelectedTVDevice] = useState(
    currentDevice && currentDevice.type === 'TV' ? currentDevice : null
  );
  const [tvSelectedInput, setTVSelectedInput] = useState('hdmi1');
  const [tvCommandResponse, setTVCommandResponse] = useState('');

  // Routine modal state (left intact, but now used only within the modal)
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  const [routineTab, setRoutineTab] = useState("startup"); // "startup" or "shutdown"
  const [startupRoutine, setStartupRoutine] = useState([]); // array of { command: string, wait: number }
  const [shutdownRoutine, setShutdownRoutine] = useState([]);

  // New state for inserting a device command
  const [deviceCmdTarget, setDeviceCmdTarget] = useState("");
  const [deviceCmdAction, setDeviceCmdAction] = useState("");
  const [deviceCmdWait, setDeviceCmdWait] = useState("");

  // Disable routine buttons while executing a routine.
  const [isExecutingRoutine, setIsExecutingRoutine] = useState(false);

  // When currentArea is passed, fetch its devices (used in the modal)
  const fetchAreaDevices = async () => {
    if (!currentArea) return;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/areas/${currentArea.id}/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("[GoogleHomeDevices] Fetched area devices:", res.data.devices);
      setDevices(res.data.devices || []);
    } catch (error) {
      console.error("[GoogleHomeDevices] Error fetching area devices:", error);
    }
  };

  useEffect(() => {
    if (currentArea) {
      fetchAreaDevices();
    }
  }, [currentArea]);

  // Load routines from currentArea if available
  useEffect(() => {
    if (currentArea && currentArea.startup_shutdown_routines) {
      try {
        const routines = typeof currentArea.startup_shutdown_routines === "object" 
          ? currentArea.startup_shutdown_routines 
          : JSON.parse(currentArea.startup_shutdown_routines);
        setStartupRoutine(routines.startup || []);
        setShutdownRoutine(routines.shutdown || []);
        console.log("[GoogleHomeDevices] Loaded routines:", routines);
      } catch (e) {
        console.error("[GoogleHomeDevices] Error parsing routines:", e);
      }
    }
  }, [currentArea]);

  useEffect(() => {
    if (currentArea && currentArea.site_id) {
      const token = localStorage.getItem('token');
      axios.get(`${process.env.REACT_APP_API_URL}/googlehome/status?site=${currentArea.site_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setHomeNeedsReauth(!!res.data.google_home_needs_reauth);
        setAssistantNeedsReauth(!!res.data.google_assistant_needs_reauth);
        if (res.data.google_home) {
          localStorage.setItem('googleConnected', 'true');
          setIsConnected(true);
        } else {
          localStorage.removeItem('googleConnected');
          setIsConnected(false);
        }
        if (res.data.google_assistant) {
          localStorage.setItem('assistantConnected', 'true');
          setAssistantConnected(true);
        } else {
          localStorage.removeItem('assistantConnected');
          setAssistantConnected(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching Google connection status:", err);
      });
    }
  }, [currentArea]);
  

  useEffect(() => {
    if (currentDevice && currentDevice.type === 'TV') {
      console.log("[GoogleHomeDevices] currentDevice updated. Using TV:", currentDevice);
      setSelectedTVDevice(currentDevice);
    } else {
      console.log("[GoogleHomeDevices] No currentDevice of type TV provided.");
    }
  }, [currentDevice]);

  const fetchDevices = () => {
    setLoadingDevices(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${process.env.REACT_APP_API_URL}/googlehome/googlehomedevices`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log("[GoogleHomeDevices] Fetched devices:", res.data.devices);
        setDevices(res.data.devices || []);
        setLoadingDevices(false);
      })
      .catch((err) => {
        console.error("[GoogleHomeDevices] Error fetching devices:", err);
        setLoadingDevices(false);
      });
  };

  const testGetDevices = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/googlehome/googlehomedevices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("[GoogleHomeDevices] Test GET devices response:", res.data);
      setGlobalResponse('Test GET Devices result:\n' + JSON.stringify(res.data, null, 2));
    } catch (err) {
      console.error("[GoogleHomeDevices] Test GET devices error:", err);
      setGlobalResponse('Test GET Devices error: ' + err.message);
    }
  };

  const handleConnect = async () => {
    const token = localStorage.getItem('token');
    // Append the site parameter from currentArea
    if (!currentArea || !currentArea.site_id) {
      setGlobalResponse('Please select an area first.');
      return;
    }
    const siteParam = `?site=${currentArea.site_id}`;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/googlehome/auth-url${siteParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { authUrl } = response.data;
      console.log("[GoogleHomeDevices] Redirecting to authUrl:", authUrl);
      window.location.href = authUrl;
    } catch (error) {
      console.error("[GoogleHomeDevices] Error fetching auth URL:", error);
      setGlobalResponse('Failed to get Google Home auth URL.');
    }
  };
  
  const handleAssistantConnect = async () => {
    const token = localStorage.getItem('token');
    // Append the site parameter from currentArea
    if (!currentArea || !currentArea.site_id) {
      setGlobalResponse('Please select an area first.');
      return;
    }
    const siteParam = `?site=${currentArea.site_id}`;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/googlehome/assistant-auth-url${siteParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { authUrl } = response.data;
      console.log("[GoogleHomeDevices] Redirecting to assistant authUrl:", authUrl);
      window.location.href = authUrl;
    } catch (error) {
      console.error("[GoogleHomeDevices] Error fetching assistant auth URL:", error);
      setGlobalResponse('Failed to get Google Assistant auth URL.');
    }
  };

  useEffect(() => {
    if (!assistantLoginTrigger) return;
    handleAssistantConnect();
  }, [assistantLoginTrigger]);
  

  const sendGlobalCommand = async (command) => {
    console.log("[GoogleHomeDevices] Sending global command:", command);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/googlehome/assistant-query`,
        { query: command, siteId: currentArea?.site_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[GoogleHomeDevices] Global command response:", res.data.response);
      setGlobalResponse('Global command response:\n' + JSON.stringify(res.data.response, null, 2));
    } catch (err) {
      console.error("[GoogleHomeDevices] Error sending global command:", err);
      setGlobalResponse('Error sending global command: ' + err.message);
    }
  };

  const sendTVCommand = async (command) => {
    console.log("[GoogleHomeDevices] Sending TV command:", command);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/googlehome/assistant-query`,
        { query: command, siteId: currentArea?.site_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[GoogleHomeDevices] TV command response:", res.data.response);
      setTVCommandResponse('TV command response:\n' + JSON.stringify(res.data.response, null, 2));
    } catch (err) {
      console.error("[GoogleHomeDevices] Error sending TV command:", err);
      setTVCommandResponse('Error sending TV command: ' + err.message);
    }
  };

  // Execute routine commands sequentially and disable routine buttons while executing.
  const executeRoutine = (routine) => {
    if (!routine || routine.length === 0) {
      console.log("[GoogleHomeDevices] No routine commands to execute.");
      return;
    }
    setIsExecutingRoutine(true);
    let index = 0;
    const executeNext = () => {
      if (index >= routine.length) {
        setIsExecutingRoutine(false);
        return;
      }
      const cmd = routine[index];
      console.log(`[GoogleHomeDevices] Executing command: ${cmd.command}, waiting ${cmd.wait} seconds`);
      sendGlobalCommand(cmd.command);
      index++;
      setTimeout(executeNext, cmd.wait * 1000);
    };
    executeNext();
  };

  const handleStartupAll = () => {
    console.log("[GoogleHomeDevices] Executing Startup Routine");
    executeRoutine(startupRoutine);
  };

  const handleShutdownAll = () => {
    console.log("[GoogleHomeDevices] Executing Shutdown Routine");
    executeRoutine(shutdownRoutine);
  };

  // Modal controls
  const openRoutineModal = () => setShowRoutineModal(true);
  const closeRoutineModal = () => setShowRoutineModal(false);

  // Insert device command from modal drop downs plus wait input.
  const insertDeviceCommand = () => {
    if (!currentArea) {
      alert("No area selected. Cannot insert device command.");
      return;
    }
    const targetDevice = areaDevices.find(d => d.id.toString() === deviceCmdTarget);
    if (!targetDevice || !deviceCmdAction) {
      alert("Please select both a target device and an action.");
      return;
    }
    const waitTime = parseInt(deviceCmdWait);
    const cmdText = `${deviceCmdAction} ${targetDevice.name}`;
    const newLine = { command: cmdText, wait: isNaN(waitTime) ? 0 : waitTime };
    if (routineTab === "startup") {
      setStartupRoutine([...startupRoutine, newLine]);
    } else {
      setShutdownRoutine([...shutdownRoutine, newLine]);
    }
    setDeviceCmdTarget("");
    setDeviceCmdAction("");
    setDeviceCmdWait("");
  };

  const addRoutineLine = (type) => {
    if (type === "startup") {
      setStartupRoutine([...startupRoutine, { command: "", wait: 0 }]);
    } else {
      setShutdownRoutine([...shutdownRoutine, { command: "", wait: 0 }]);
    }
  };

  const updateRoutineLine = (type, index, field, value) => {
    if (type === "startup") {
      const newRoutine = [...startupRoutine];
      newRoutine[index][field] = field === "wait" ? parseInt(value) : value;
      setStartupRoutine(newRoutine);
    } else {
      const newRoutine = [...shutdownRoutine];
      newRoutine[index][field] = field === "wait" ? parseInt(value) : value;
      setShutdownRoutine(newRoutine);
    }
  };

  const removeRoutineLine = (type, index) => {
    if (type === "startup") {
      const newRoutine = [...startupRoutine];
      newRoutine.splice(index, 1);
      setStartupRoutine(newRoutine);
    } else {
      const newRoutine = [...shutdownRoutine];
      newRoutine.splice(index, 1);
      setShutdownRoutine(newRoutine);
    }
  };

  const saveRoutines = async () => {
    if (!currentArea) {
      alert("No area selected. Cannot save routines.");
      return;
    }
    const payload = { routines: { startup: startupRoutine, shutdown: shutdownRoutine } };
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/areas/${currentArea.id}/routines`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[GoogleHomeDevices] Routines saved:", res.data);
      closeRoutineModal();
    } catch (error) {
      console.error("[GoogleHomeDevices] Error saving routines:", error);
      alert("Error saving routines: " + error.message);
    }
  };

  const handleTVDeviceSelect = (e) => {
    const deviceId = e.target.value;
    console.log("[GoogleHomeDevices] TV device selected from dropdown:", deviceId);
    const device = tvDevicesAll.find((d) => d.id.toString() === deviceId);
    console.log("[GoogleHomeDevices] Found device from dropdown:", device);
    setSelectedTVDevice(device);
  };

  // Handle Logouts of google home and assistant
  const handleGoogleLogout = async () => {
    const token = localStorage.getItem('token');
    if (!currentArea || !currentArea.site_id) {
      localStorage.removeItem('googleConnected');
      setIsConnected(false);
      setGlobalResponse('Logged out from Google Home.');
      console.log("[GoogleHomeDevices] Logged out from Google Home (no current area).");
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/googlehome/logout`,
        { provider: 'google_home', site: currentArea.site_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.removeItem('googleConnected');
      setIsConnected(false);
      setGlobalResponse('Logged out from Google Home.');
      console.log("[GoogleHomeDevices] Logged out from Google Home.");
    } catch (error) {
      console.error("[GoogleHomeDevices] Error logging out from Google Home:", error);
      setGlobalResponse('Error logging out from Google Home: ' + error.message);
    }
  };
  
  const handleAssistantLogout = async () => {
    const token = localStorage.getItem('token');
    if (!currentArea || !currentArea.site_id) {
      localStorage.removeItem('assistantConnected');
      setAssistantConnected(false);
      setGlobalResponse('Logged out from Google Assistant.');
      console.log("[GoogleHomeDevices] Logged out from Google Assistant (no current area).");
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/googlehome/logout`,
        { provider: 'google_assistant', site: currentArea.site_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.removeItem('assistantConnected');
      setAssistantConnected(false);
      setGlobalResponse('Logged out from Google Assistant.');
      console.log("[GoogleHomeDevices] Logged out from Google Assistant.");
    } catch (error) {
      console.error("[GoogleHomeDevices] Error logging out from Google Assistant:", error);
      setGlobalResponse('Error logging out from Google Assistant: ' + error.message);
    }
  };

  // Devices for the modal are those in the current area.
  const areaDevices = currentArea ? devices.filter(d => d.area_id === currentArea.id) : [];

  // Also filter TV devices for the main TV controls.
  const tvDevicesAll = devices.filter((d) => d.type === 'TV');
  const connectedDevicesNonTV = devices.filter((d) => d.status === 'active' && d.type !== 'TV');
  const otherDevicesNonTV = devices.filter((d) => d.status !== 'active' && d.type !== 'TV');

  return (
    <div className="google-home-devices">
      <h2>Google Home Devices</h2>
      {(assistantNeedsReauth || homeNeedsReauth) && (
        <div className="reauth-banner">
          {assistantNeedsReauth && (
            <div className="reauth-row">
              <span>Google Assistant needs re-authorization.</span>
              <button onClick={handleAssistantConnect}>Re-link Assistant</button>
            </div>
          )}
          {homeNeedsReauth && (
            <div className="reauth-row">
              <span>Google Home needs re-authorization.</span>
              <button onClick={handleConnect}>Re-link Home</button>
            </div>
          )}
        </div>
      )}
      <div className="connection-info">
        <div className="home-connection">
          <strong>Google Home:</strong>{" "}
          {isConnected ? (
            <>
              <span>Connected</span>
              <button onClick={fetchDevices}>Refresh Devices</button>
              <button onClick={testGetDevices}>Test GET Devices</button>
              <button onClick={handleGoogleLogout}>Logout Google Home</button>
            </>
          ) : (
            <>
              <span>Not Connected</span>
              <button onClick={handleConnect}>Sign in to Google Home</button>
            </>
          )}
        </div>
        <div className="assistant-connection">
          <strong>Google Assistant:</strong>{" "}
          {assistantConnected ? (
            <>
              <span>Connected</span>
              <button onClick={handleAssistantLogout}>Logout Google Assistant</button>
            </>
          ) : (
            <button onClick={handleAssistantConnect}>Sign in to Google Assistant</button>
          )}
        </div>
      </div>
      {loadingDevices ? (
        <p>Loading devices...</p>
      ) : (
        <>
          {connectedDevicesNonTV.length > 0 && (
            <>
              <h3>Connected Devices</h3>
              <ul>
                {connectedDevicesNonTV.map((dev) => (
                  <li key={dev.id}>
                    {dev.name} ({dev.type})
                  </li>
                ))}
              </ul>
            </>
          )}
          {otherDevicesNonTV.length > 0 && (
            <>
              <h3>Other Devices</h3>
              <ul>
                {otherDevicesNonTV.map((dev) => (
                  <li key={dev.id}>
                    {dev.name} ({dev.type}){' '}
                    <button onClick={() => console.log(`Setup for ${dev.name} initiated.`)}>
                      Setup
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {devices.length === 0 && <p>No devices found. Please refresh or test.</p>}
        </>
      )}
      <div className="tv-controls">
        <h3>
          TV Controls for {selectedTVDevice ? selectedTVDevice.name : 'None Selected'}
        </h3>
        {!currentDevice && (
          <label>
            Select TV Device:&nbsp;
            <select value={selectedTVDevice ? selectedTVDevice.id : ''} onChange={handleTVDeviceSelect}>
              <option value="">Select a device</option>
              {tvDevicesAll.map((dev) => (
                <option key={dev.id} value={dev.id}>
                  {dev.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {selectedTVDevice ? (
          <>
            <div className="tv-button-group">
              <button onClick={() => sendTVCommand(`Power on ${selectedTVDevice.name}`)}>
                Turn On
              </button>
              <button onClick={() => sendTVCommand(`Power off ${selectedTVDevice.name}`)}>
                Turn Off
              </button>
              <label>
                Change input to:&nbsp;
                <select value={tvSelectedInput} onChange={(e) => setTVSelectedInput(e.target.value)}>
                  <option value="hdmi1">HDMI 1</option>
                  <option value="hdmi2">HDMI 2</option>
                  <option value="hdmi3">HDMI 3</option>
                  <option value="hdmi4">HDMI 4</option>
                </select>
              </label>
              <button
                onClick={() =>
                  sendTVCommand(`Set the input on ${selectedTVDevice.name} to ${tvSelectedInput.toUpperCase()}`)
                }
              >
                Change Input
              </button>
            </div>
            <div className="tv-button-group">
              <button onClick={() => sendTVCommand(`Mute ${selectedTVDevice.name}`)}>Mute</button>
              <button onClick={() => sendTVCommand(`Unmute ${selectedTVDevice.name}`)}>Unmute</button>
              <button onClick={() => sendTVCommand(`Volume up on ${selectedTVDevice.name}`)}>
                Volume Up
              </button>
              <button onClick={() => sendTVCommand(`Volume down on ${selectedTVDevice.name}`)}>
                Volume Down
              </button>
            </div>
            <div className="tv-button-group">
              <button onClick={() => sendTVCommand(`Channel up on ${selectedTVDevice.name}`)}>
                Channel Up
              </button>
              <button onClick={() => sendTVCommand(`Channel down on ${selectedTVDevice.name}`)}>
                Channel Down
              </button>
              <button onClick={() => sendTVCommand(`Launch Youtube on ${selectedTVDevice.name}`)}>
                Launch Youtube
              </button>
              <button onClick={() => sendTVCommand(`Launch Suitestream on ${selectedTVDevice.name}`)}>
                Launch Suitestream
              </button>
            </div>
            {tvCommandResponse && <pre className="tv-response">{tvCommandResponse}</pre>}
          </>
        ) : (
          <p>No TV device selected.</p>
        )}
      </div>
      <AssistantQuery siteId={currentArea?.site_id} />
      {showRoutineModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Modify Startup / Shutdown Routines</h3>
            {!currentArea ? (
              <p style={{ color: "red" }}>No area selected. Please select an area in the Control Panel.</p>
            ) : (
              <>
                <div className="routine-tabs">
                  <button
                    className={routineTab === "startup" ? "active-tab" : ""}
                    onClick={() => setRoutineTab("startup")}
                  >
                    Startup Routine
                  </button>
                  <button
                    className={routineTab === "shutdown" ? "active-tab" : ""}
                    onClick={() => setRoutineTab("shutdown")}
                  >
                    Shutdown Routine
                  </button>
                </div>
                <div className="insert-device-command">
                  <p>Insert Device Command:</p>
                  <div className="command-row">
                    <select
                      value={deviceCmdTarget}
                      onChange={(e) => setDeviceCmdTarget(e.target.value)}
                    >
                      <option value="">Select Device</option>
                      {areaDevices.map((dev) => (
                        <option key={dev.id} value={dev.id}>
                          {dev.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={deviceCmdAction}
                      onChange={(e) => setDeviceCmdAction(e.target.value)}
                    >
                      <option value="">Select Action</option>
                      <option value="Turn on">Turn on</option>
                      <option value="Turn off">Turn off</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Then wait for (seconds)"
                      value={deviceCmdWait}
                      onChange={(e) => setDeviceCmdWait(e.target.value)}
                    />
                    <button onClick={insertDeviceCommand}>Insert</button>
                  </div>
                </div>
                <div className="routine-editor">
                  {routineTab === "startup" &&
                    startupRoutine.map((line, idx) => (
                      <div key={idx} className="routine-line">
                        <input
                          type="text"
                          placeholder="Enter command (e.g. Power off Guest TV)"
                          value={line.command}
                          onChange={(e) => updateRoutineLine("startup", idx, "command", e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Wait (seconds)"
                          value={line.wait}
                          onChange={(e) => updateRoutineLine("startup", idx, "wait", e.target.value)}
                        />
                        <button onClick={() => removeRoutineLine("startup", idx)}>Remove</button>
                      </div>
                    ))}
                  {routineTab === "shutdown" &&
                    shutdownRoutine.map((line, idx) => (
                      <div key={idx} className="routine-line">
                        <input
                          type="text"
                          placeholder="Enter command (e.g. Power off Guest TV)"
                          value={line.command}
                          onChange={(e) => updateRoutineLine("shutdown", idx, "command", e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Wait (seconds)"
                          value={line.wait}
                          onChange={(e) => updateRoutineLine("shutdown", idx, "wait", e.target.value)}
                        />
                        <button onClick={() => removeRoutineLine("shutdown", idx)}>Remove</button>
                      </div>
                    ))}
                  <button onClick={() => addRoutineLine(routineTab)}>Add Custom Command</button>
                </div>
                <div className="modal-actions">
                  <button onClick={saveRoutines}>Save</button>
                  <button onClick={closeRoutineModal}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleHomeDevices;
